import { useEffect, useRef, useState } from "react";
import type { CSSProperties, FC } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as Flags from "country-flag-icons/react/3x2";
import type { MediaItem } from "@tg/shared";
import { api } from "../lib/api";
import { COUNTRIES, flagCode, teamFlag } from "../lib/countries";
import { kvGet, kvSet, KEYS } from "../lib/idb";
import { downloadBlob, renderNodeToPng } from "../cupon/render";
import { Banner, Button, Card, Input, cn } from "../ui";

const FlagMap = Flags as unknown as Record<string, FC<{ title?: string; style?: CSSProperties }>>;

function FlagImg({ code, style }: { code: string; style?: CSSProperties }) {
  const F = FlagMap[flagCode(code)];
  if (!F) return <span style={{ width: 22, height: 15, background: "#3a4d5a", borderRadius: 2, display: "inline-block", ...style }} />;
  return <F style={{ borderRadius: 2, ...style }} />;
}

const SPORTS = [
  { key: "futbol", label: "Fútbol" },
  { key: "basket", label: "Baloncesto" },
  { key: "tenis", label: "Tenis" },
] as const;

function SportIcon({ sport }: { sport: string }) {
  const c = "#8aa0ac";
  if (sport === "basket")
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="none" stroke={c} strokeWidth="1.6" />
        <path d="M3 12h18M12 3v18M5 5c4 3 4 11 0 14M19 5c-4 3-4 11 0 14" fill="none" stroke={c} strokeWidth="1.2" />
      </svg>
    );
  if (sport === "tenis")
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="none" stroke={c} strokeWidth="1.6" />
        <path d="M5 6c5 2 9 6 11 12M19 6c-5 2-9 6-11 12" fill="none" stroke={c} strokeWidth="1.2" />
      </svg>
    );
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke={c} strokeWidth="1.6" />
      <path d="M12 7l3 2-1 4h-4l-1-4z" fill={c} />
    </svg>
  );
}

interface Leg {
  id: string;
  sport: string;
  selection: string;
  market: string;
  odds: string;
  datetime: string;
  home: { name: string; code: string };
  away: { name: string; code: string };
}
interface CuponState {
  legs: Leg[];
  stake: string;
  currencySymbol: string;
  currencyCountry: string;
  showCashout: boolean;
  cashout: string;
}

const MAX_LEGS = 8;
let seq = 0;
function newLeg(): Leg {
  seq += 1;
  return {
    id: `leg-${Date.now().toString(36)}-${seq}`,
    sport: "futbol",
    selection: "",
    market: "",
    odds: "",
    datetime: "",
    home: { name: "", code: "ES" },
    away: { name: "", code: "MX" },
  };
}

const DEFAULT_STATE: CuponState = {
  legs: [newLeg()],
  stake: "10500",
  currencySymbol: "MX$",
  currencyCountry: "MX",
  showCashout: true,
  cashout: "",
};

function parseNum(s: string): number {
  const n = parseFloat((s || "").replace(/\./g, "").replace(",", "."));
  return isNaN(n) ? 0 : n;
}
function parseOdds(s: string): number {
  const n = parseFloat((s || "").replace(",", "."));
  return isNaN(n) || n <= 0 ? 0 : n;
}
function money(n: number, symbol: string): string {
  return symbol + new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}
function oddsFmt(n: number): string {
  return new Intl.NumberFormat("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}
interface ImportLeg {
  sport?: string;
  selection?: string;
  market?: string;
  odds?: string;
  datetime?: string;
  home?: { name?: string };
  away?: { name?: string };
}
interface ImportPayload {
  v?: number;
  stake?: string;
  currencySymbol?: string;
  currencyCountry?: string;
  legs?: ImportLeg[];
}

function b64DecodeUtf8(b64: string): string {
  return decodeURIComponent(
    Array.prototype.map
      .call(atob(b64), (c: string) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
      .join(""),
  );
}

// The Chrome extension hands off a parlay via #import=<base64 json>.
function readImport(): ImportPayload | null {
  const m = /^#import=(.+)$/.exec(location.hash);
  if (!m) return null;
  try {
    return JSON.parse(b64DecodeUtf8(decodeURIComponent(m[1]!))) as ImportPayload;
  } catch {
    return null;
  }
}

function dateFmt(dt: string): string {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  const day = new Intl.DateTimeFormat("es-ES", { weekday: "short", day: "2-digit", month: "short" }).format(d);
  const time = new Intl.DateTimeFormat("es-ES", { hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  return `${day} · ${time}`;
}

export function Cupon() {
  const qc = useQueryClient();
  const [st, setSt] = useState<CuponState>(DEFAULT_STATE);
  const [logo, setLogo] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<"png" | "msg" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const slipRef = useRef<HTMLDivElement>(null);

  const [imported, setImported] = useState(false);

  useEffect(() => {
    void (async () => {
      const saved = await kvGet<CuponState>(KEYS.cupon);
      let base: CuponState = saved && saved.legs?.length ? saved : DEFAULT_STATE;

      const imp = readImport();
      if (imp && imp.legs?.length) {
        base = {
          ...base,
          stake: imp.stake ?? base.stake,
          currencySymbol: imp.currencySymbol ?? base.currencySymbol,
          currencyCountry: imp.currencyCountry ?? base.currencyCountry,
          legs: imp.legs.map((l) => ({
            ...newLeg(),
            sport: l.sport ?? "futbol",
            selection: l.selection ?? "",
            market: l.market ?? "",
            odds: l.odds ?? "",
            datetime: l.datetime ?? "",
            home: { name: l.home?.name ?? "", code: teamFlag(l.home?.name ?? "") ?? "ES" },
            away: { name: l.away?.name ?? "", code: teamFlag(l.away?.name ?? "") ?? "MX" },
          })),
        };
        history.replaceState(null, "", location.pathname);
        setImported(true);
      }
      setSt(base);
      const l = await kvGet<string>(KEYS.cuponLogo);
      if (l) setLogo(l);
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => kvSet(KEYS.cupon, st), 400);
    return () => clearTimeout(t);
  }, [st, loaded]);

  const combined = st.legs.reduce((acc, l) => acc * (parseOdds(l.odds) || 1), 1);
  const stake = parseNum(st.stake);
  const payout = stake * combined;

  function patchLeg(id: string, patch: Partial<Leg>) {
    setSt((s) => ({ ...s, legs: s.legs.map((l) => (l.id === id ? { ...l, ...patch } : l)) }));
  }
  function addLeg() {
    setSt((s) => (s.legs.length >= MAX_LEGS ? s : { ...s, legs: [...s.legs, newLeg()] }));
  }
  function removeLeg(id: string) {
    setSt((s) => ({ ...s, legs: s.legs.length > 1 ? s.legs.filter((l) => l.id !== id) : s.legs }));
  }

  async function onLogo(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      setLogo(url);
      kvSet(KEYS.cuponLogo, url);
    };
    reader.readAsDataURL(file);
  }

  async function exportPng(): Promise<Blob | null> {
    if (!slipRef.current) return null;
    return renderNodeToPng(slipRef.current);
  }
  async function onDownload() {
    setBusy("png");
    try {
      const blob = await exportPng();
      if (blob) downloadBlob(blob, "cupon.png");
    } finally {
      setBusy(null);
    }
  }
  async function onUseInMessage() {
    setBusy("msg");
    setMsg(null);
    try {
      const blob = await exportPng();
      if (!blob) return;
      const draft = await api.getDraft();
      const res = await api.uploadImage(blob, "image/png");
      const item: MediaItem = { key: res.imageKey, w: res.w, h: res.h, mime: "image/png", size: res.size };
      await api.putDraft({ media: [...draft.media, item] });
      qc.invalidateQueries({ queryKey: ["draft"] });
      setMsg("Cupón añadido al álbum del mensaje (pestaña Redactar).");
    } catch (e) {
      setMsg(`Error: ${String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Cupón (simulador de pronóstico)</h1>
      </div>
      <Banner kind="info">
        Genera una imagen tipo cupón con tu logo, banderas reales y cuota/pago calculados, y úsala como imagen del
        mensaje. Con la extensión de Chrome puedes importar tus selecciones reales desde Stake.
      </Banner>
      {imported && (
        <Banner kind="success">
          Selecciones importadas desde Stake. Revisa banderas (país de cada equipo) y las cantidades antes de generar.
        </Banner>
      )}

      <Card className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-slate-300">Extensión de Chrome (importar desde Stake)</h2>
          <a
            href="/tg-pronostico-extension.zip"
            download
            className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-500"
          >
            ⬇ Descargar extensión (.zip)
          </a>
        </div>
        <ol className="list-decimal space-y-1 pl-5 text-xs text-slate-400">
          <li>Descarga el .zip y descomprímelo.</li>
          <li>Abre <code>chrome://extensions</code> → activa <strong>Modo de desarrollador</strong>.</li>
          <li><strong>Cargar descomprimida</strong> → elige la carpeta <code>tg-pronostico-extension</code>.</li>
          <li>Entra en Stake (logueado), monta tu parlay y pulsa <strong>"Crear pronóstico"</strong> (abajo a la derecha).</li>
        </ol>
        <p className="text-xs text-slate-500">
          Chrome no permite instalar extensiones directamente desde una web (solo Web Store o "cargar descomprimida"),
          por eso se descarga el .zip.
        </p>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* builder */}
        <div className="space-y-3">
          <Card className="space-y-3">
            <h2 className="text-sm font-medium text-slate-300">Apuesta y divisa</h2>
            <div className="grid grid-cols-3 gap-2">
              <label className="col-span-1 space-y-1 text-xs">
                <span className="text-slate-400">Símbolo</span>
                <Input value={st.currencySymbol} onChange={(e) => setSt((s) => ({ ...s, currencySymbol: e.target.value }))} />
              </label>
              <label className="col-span-1 space-y-1 text-xs">
                <span className="text-slate-400">Bandera divisa</span>
                <select
                  className="w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-2 text-sm"
                  value={st.currencyCountry}
                  onChange={(e) => setSt((s) => ({ ...s, currencyCountry: e.target.value }))}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </label>
              <label className="col-span-1 space-y-1 text-xs">
                <span className="text-slate-400">Apuesta</span>
                <Input value={st.stake} onChange={(e) => setSt((s) => ({ ...s, stake: e.target.value }))} />
              </label>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input type="checkbox" checked={st.showCashout} onChange={(e) => setSt((s) => ({ ...s, showCashout: e.target.checked }))} />
              Mostrar fila "Retirarse"
            </label>
            {st.showCashout && (
              <label className="space-y-1 text-xs">
                <span className="text-slate-400">Importe "Retirarse" (vacío = igual a la apuesta)</span>
                <Input value={st.cashout} onChange={(e) => setSt((s) => ({ ...s, cashout: e.target.value }))} placeholder={st.stake} />
              </label>
            )}
          </Card>

          <Card className="space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-slate-300">Selecciones ({st.legs.length}/{MAX_LEGS})</h2>
              <span className="text-xs text-slate-500">Cuota combinada: <strong className="text-sky-400">{oddsFmt(combined)}</strong></span>
            </div>
            {st.legs.map((l, i) => (
              <div key={l.id} className="space-y-2 rounded-md border border-slate-800 p-2">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <span>#{i + 1}</span>
                  {st.legs.length > 1 && (
                    <button className="text-red-400" onClick={() => removeLeg(l.id)}>quitar</button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <select
                    className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm"
                    value={l.sport}
                    onChange={(e) => patchLeg(l.id, { sport: e.target.value })}
                  >
                    {SPORTS.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
                  </select>
                  <Input placeholder="Cuota (1,58)" value={l.odds} onChange={(e) => patchLeg(l.id, { odds: e.target.value })} />
                  <Input className="col-span-2" placeholder="Selección (p. ej. Suiza)" value={l.selection} onChange={(e) => patchLeg(l.id, { selection: e.target.value })} />
                  <Input className="col-span-2" placeholder="Mercado (p. ej. 1ª mitad · 1x2)" value={l.market} onChange={(e) => patchLeg(l.id, { market: e.target.value })} />
                  <Input className="col-span-2" type="datetime-local" value={l.datetime} onChange={(e) => patchLeg(l.id, { datetime: e.target.value })} />
                  <div className="flex gap-1">
                    <select className="w-20 rounded-md border border-slate-700 bg-slate-900 px-1 py-1.5 text-xs" value={l.home.code} onChange={(e) => patchLeg(l.id, { home: { ...l.home, code: e.target.value } })}>
                      {COUNTRIES.map((c) => (<option key={c.code} value={c.code}>{c.code}</option>))}
                    </select>
                    <Input placeholder="Local" value={l.home.name} onChange={(e) => patchLeg(l.id, { home: { ...l.home, name: e.target.value } })} />
                  </div>
                  <div className="flex gap-1">
                    <select className="w-20 rounded-md border border-slate-700 bg-slate-900 px-1 py-1.5 text-xs" value={l.away.code} onChange={(e) => patchLeg(l.id, { away: { ...l.away, code: e.target.value } })}>
                      {COUNTRIES.map((c) => (<option key={c.code} value={c.code}>{c.code}</option>))}
                    </select>
                    <Input placeholder="Visitante" value={l.away.name} onChange={(e) => patchLeg(l.id, { away: { ...l.away, name: e.target.value } })} />
                  </div>
                </div>
              </div>
            ))}
            {st.legs.length < MAX_LEGS && (
              <Button variant="secondary" onClick={addLeg}>+ añadir selección</Button>
            )}
          </Card>

          <Card className="space-y-2">
            <h2 className="text-sm font-medium text-slate-300">Logo</h2>
            <input type="file" accept="image/*" className="text-xs text-slate-400" onChange={(e) => e.target.files?.[0] && onLogo(e.target.files[0])} />
            <p className="text-xs text-slate-500">Sube tu logo (se guarda en este navegador y se incrusta en el cupón).</p>
          </Card>
        </div>

        {/* preview + actions */}
        <div className="space-y-3 self-start">
          <div className="flex justify-center rounded-lg bg-slate-800/40 p-4">
            <div
              ref={slipRef}
              style={{
                width: 360,
                background: "#1b2a36",
                borderRadius: 14,
                padding: "18px 18px 16px",
                color: "#e9f0f4",
                fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
              }}
            >
              {st.legs.map((l, i) => (
                <div key={l.id} style={{ paddingTop: i ? 12 : 0, paddingBottom: 12, borderTop: i ? "1px solid #2a3a47" : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 17, fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                      <SportIcon sport={l.sport} /> {l.selection || "Selección"}
                    </span>
                    <span style={{ fontSize: 17, fontWeight: 500, color: "#4aa3ff" }}>{l.odds ? oddsFmt(parseOdds(l.odds)) : "—"}</span>
                  </div>
                  {l.market && <div style={{ fontSize: 13, color: "#8aa0ac", marginTop: 4 }}>{l.market}</div>}
                  {l.datetime && (
                    <div style={{ fontSize: 13, color: "#8aa0ac", marginTop: 8 }}>{dateFmt(l.datetime)}</div>
                  )}
                  <div style={{ marginTop: 8, fontSize: 15, display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}><FlagImg code={l.home.code} style={{ width: 22, height: 15 }} />{l.home.name || "Local"}</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}><FlagImg code={l.away.code} style={{ width: 22, height: 15 }} />{l.away.name || "Visitante"}</span>
                  </div>
                </div>
              ))}

              {st.legs.length > 1 && (
                <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #2a3a47", paddingTop: 10, fontSize: 14 }}>
                  <span style={{ color: "#8aa0ac" }}>Cuota combinada</span>
                  <span style={{ fontWeight: 500, color: "#4aa3ff" }}>{oddsFmt(combined)}</span>
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0" }}>
                <div style={{ flex: 1, height: 1, background: "#2a3a47" }} />
                {logo ? (
                  <img src={logo} alt="" style={{ maxHeight: 26, maxWidth: 120, objectFit: "contain" }} />
                ) : (
                  <span style={{ fontSize: 12, color: "#5f7682", border: "1px dashed #3a4d5a", borderRadius: 6, padding: "2px 10px" }}>tu logo</span>
                )}
                <div style={{ flex: 1, height: 1, background: "#2a3a47" }} />
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, marginBottom: 6 }}>
                <span style={{ color: "#8aa0ac" }}>Apuesta</span>
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}><FlagImg code={st.currencyCountry} style={{ width: 20, height: 14 }} />{money(stake, st.currencySymbol)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, marginBottom: st.showCashout ? 14 : 0 }}>
                <span style={{ color: "#8aa0ac" }}>Pago</span>
                <span style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}><FlagImg code={st.currencyCountry} style={{ width: 20, height: 14 }} />{money(payout, st.currencySymbol)}</span>
              </div>
              {st.showCashout && (
                <div style={{ background: "#2a3f4d", borderRadius: 10, textAlign: "center", padding: 12, fontSize: 15, fontWeight: 500 }}>
                  Retirarse {money(parseNum(st.cashout) || stake, st.currencySymbol)}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={onUseInMessage} disabled={busy !== null}>
              {busy === "msg" ? "Añadiendo…" : "Usar en el mensaje"}
            </Button>
            <Button variant="secondary" onClick={onDownload} disabled={busy !== null}>
              {busy === "png" ? "Generando…" : "Descargar PNG"}
            </Button>
          </div>
          {msg && <Banner kind={msg.startsWith("Error") ? "danger" : "success"}>{msg}</Banner>}
        </div>
      </div>
    </div>
  );
}
